import { useCallback } from "react";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setFeature } from "src/redux/slices/generatorSlice";

/**
 * Custom hook to handle show/hide content functionality based on checkbox state.
 * When a checkbox with feature="showContent" is checked, it shows all elements with the matching onClass.
 * When unchecked, it hides elements with the matching onClass.
 */
export const useShowContent = () => {
  const dispatch = useDispatch();

  const showContent = useSelector(
    state => state.generatorSlice.features?.showContent,
  );

  /**
   * Handles checkbox changes for showContent feature
   * @param checked - Whether the checkbox is checked
   * @param className - The onClass name to show/hide
   * @param unique - Optional index for reports with multiple entries
   */
  const handleShowContent = useCallback(
    (
      checked: boolean | string,
      className: string,
      unique?: number | string,
    ) => {
      const normalizedChecked = checked === "true" || checked === true;
      const key =
        unique !== undefined ? `${className}_${unique}` : `${className}`;

      if (showContent?.[key] === normalizedChecked) return;

      dispatch(
        setFeature({
          key: "showContent",
          feature: { [key]: normalizedChecked },
          insert: true,
        }),
      );
    },
    [dispatch, showContent],
  );

  /**
   * Checks if content with the given onClass should be visible
   * @param className - The onClass name to check
   * @param unique - Optional unique identifier for reports with multiple entries
   * @returns boolean indicating if content should be visible
   */
  const isContentVisible = useCallback(
    (className?: string) => {
      if (!className) return true;
      // const key =
      //   unique !== undefined
      //     ? `showContent_${className}_${unique}`
      //     : `showContent_${className}`;
      return Object.entries(showContent ?? {}).some(
        ([key, value]) => key.includes(className) && value === true,
      );
    },
    [showContent],
  );

  /**
   * Determines if an element should be hidden based on its feature and onClass properties
   * @param element - The element with potential feature and onClass properties
   * @param unique - Optional unique identifier for reports with multiple entries
   * @returns boolean indicating if element should be hidden
   */
  const shouldHideElement = useCallback(
    (
      element: {
        feature?: string;
        onClass?: string;
        title?: string;
        hidden?: boolean;
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for caller signature compatibility
      unique?: number | string,
    ) => {
      // For elements with feature="showContent" and an onClass, check if their showContent feature is enabled
      if (element.feature === "showContent" && element.onClass) {
        return !isContentVisible(element.onClass);
      }

      // For elements with feature="hidden" and an onClass, check if their showContent feature is enabled
      if (element.feature === "hidden" && element.onClass) {
        const isVisible = isContentVisible(element.onClass);
        return !isVisible;
      }

      // For sections with an onClass and hidden: true property
      // Hide them by default unless the corresponding showContent feature is enabled
      // TODO remove after Chris changes section level hidden to feature="hidden"
      if (element.onClass && element.hidden === true) {
        const isVisible = isContentVisible(element.onClass);
        return !isVisible;
      }

      return false;
    },
    [isContentVisible],
  );

  return {
    handleShowContent,
    isContentVisible,
    shouldHideElement,
  };
};
